import { useState } from "react";
import { Download, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import JSZip from "jszip";

interface Creative {
  platform: string;
  format: string;
  aspect_ratio: string;
  output_url: string;
}

interface AdPreviewGalleryProps {
  creatives: Creative[];
  isLoading?: boolean;
}

const PLATFORM_COLORS: Record<string, string> = {
  instagram: "bg-gradient-to-br from-purple-500 to-pink-500",
  facebook: "bg-blue-600",
  tiktok: "bg-black",
  twitter: "bg-gray-900",
  pinterest: "bg-red-600",
  linkedin: "bg-blue-700",
};

export function AdPreviewGallery({ creatives, isLoading }: AdPreviewGalleryProps) {
  const [downloading, setDownloading] = useState(false);

  const downloadSingle = async (creative: Creative) => {
    try {
      const response = await fetch(creative.output_url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${creative.platform}-${creative.format.replace(/\s+/g, "-").toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  const downloadAll = async () => {
    if (creatives.length === 0) return;
    
    setDownloading(true);
    try {
      const zip = new JSZip();
      
      for (const creative of creatives) {
        const response = await fetch(creative.output_url);
        const blob = await response.blob();
        const filename = `${creative.platform}/${creative.format.replace(/\s+/g, "-").toLowerCase()}.png`;
        zip.file(filename, blob);
      }

      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ad-creatives-${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("ZIP download error:", error);
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
        <p className="text-muted-foreground">Génération des créatives en cours...</p>
        <p className="text-sm text-muted-foreground">Cela peut prendre quelques minutes</p>
      </div>
    );
  }

  if (creatives.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center">
          <ExternalLink className="w-8 h-8 text-muted-foreground" />
        </div>
        <p className="text-muted-foreground">
          Vos créatives apparaîtront ici après génération
        </p>
      </div>
    );
  }

  // Group by platform
  const grouped = creatives.reduce((acc, creative) => {
    if (!acc[creative.platform]) acc[creative.platform] = [];
    acc[creative.platform].push(creative);
    return acc;
  }, {} as Record<string, Creative[]>);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">
          {creatives.length} créative{creatives.length > 1 ? "s" : ""} générée{creatives.length > 1 ? "s" : ""}
        </h3>
        <Button onClick={downloadAll} disabled={downloading}>
          {downloading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Télécharger tout (ZIP)
        </Button>
      </div>

      {Object.entries(grouped).map(([platform, items]) => (
        <div key={platform} className="space-y-3">
          <div className="flex items-center gap-2">
            <div className={cn("w-6 h-6 rounded", PLATFORM_COLORS[platform])} />
            <h4 className="font-medium capitalize">{platform}</h4>
            <span className="text-sm text-muted-foreground">
              ({items.length} format{items.length > 1 ? "s" : ""})
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((creative, idx) => (
              <div
                key={idx}
                className="group relative rounded-lg overflow-hidden border border-border bg-card"
              >
                <img
                  src={creative.output_url}
                  alt={`${creative.platform} ${creative.format}`}
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => downloadSingle(creative)}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
                <div className="p-2 bg-card">
                  <p className="text-xs font-medium">{creative.format}</p>
                  <p className="text-xs text-muted-foreground">{creative.aspect_ratio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
